/**
 * Schema Deployer — Deploys converted DDL to a target database.
 *
 * Uses the ETL file server APIs (port 8600) for SQL execution.
 * Key difference from ETL pipeline: stored procedures are sent as
 * single statements, NOT split on semicolons.
 *
 * Supports: DB2, SQL Server, PostgreSQL, Oracle, MySQL
 */

import { executeSql, testConnection } from './etlFileService'

// ---------------------------------------------------------------------------
// Resolve driver key from connection config
// ---------------------------------------------------------------------------
function resolveDriverKey(driver) {
  const d = (driver || '').toLowerCase()
  if (d.includes('db2')) return 'db2'
  if (d.includes('sqlserver') || d.includes('mssql') || d.includes('sql server')) return 'sqlserver'
  if (d.includes('postgres')) return 'postgresql'
  if (d.includes('oracle')) return 'oracle'
  if (d.includes('mysql') || d.includes('maria')) return 'mysql'
  return d
}

// ---------------------------------------------------------------------------
// Build connection config for the ETL file server
// ---------------------------------------------------------------------------
function buildConnectionConfig(connection) {
  return {
    driver: connection.driver || connection.driverId || '',
    host: connection.host || connection.connectionFields?.host || '',
    port: connection.port || connection.connectionFields?.port || '',
    database: connection.database || connection.connectionFields?.database || '',
    username: connection.username || connection.connectionFields?.username || '',
    password: connection.password || connection.connectionFields?.password || '',
    schema: connection.schema || connection.connectionFields?.schema || '',
  }
}

// ---------------------------------------------------------------------------
// Execute a single SQL statement and report progress
// ---------------------------------------------------------------------------
async function executeStatement(connConfig, sql, label, onProgress, index, total) {
  const pct = Math.round(((index + 1) / total) * 100)

  try {
    const result = await executeSql({
      ...connConfig,
      statements: [sql],
      onError: 'continue',
      useTransaction: false,
    })

    const success = result?.success || (result?.results?.[0]?.success ?? false)
    const error = result?.results?.[0]?.error || null

    onProgress({
      index,
      total,
      pct,
      label,
      sql: sql.substring(0, 200),
      success,
      error,
    })

    return { success, error, label, sql }
  } catch (err) {
    onProgress({
      index,
      total,
      pct,
      label,
      sql: sql.substring(0, 200),
      success: false,
      error: err.message,
    })
    return { success: false, error: err.message, label, sql }
  }
}

// ---------------------------------------------------------------------------
// Deploy schema to target database
// ---------------------------------------------------------------------------
export async function deploySchema(convertedSchema, connection, options = {}, onProgress = () => {}) {
  const {
    schemaName = '',
    overwrite = false,
    deployTables = true,
    deployProcedures = true,
    deployIndexes = true,
  } = options

  const connConfig = buildConnectionConfig(connection)
  const driverKey = resolveDriverKey(connConfig.driver)

  // Build list of statements to execute
  const statements = []

  // --- Schema creation ---
  if (schemaName) {
    if (driverKey === 'db2') {
      statements.push({
        sql: `CREATE SCHEMA "${schemaName}" AUTHORIZATION db2inst1`,
        label: `Create schema "${schemaName}"`,
        optional: true, // may already exist
      })
      statements.push({
        sql: `SET CURRENT SCHEMA = '${schemaName}'`,
        label: `Set current schema to "${schemaName}"`,
        optional: false,
      })
    } else if (driverKey === 'postgresql') {
      statements.push({
        sql: `CREATE SCHEMA IF NOT EXISTS "${schemaName}"`,
        label: `Create schema "${schemaName}"`,
        optional: false,
      })
      statements.push({
        sql: `SET search_path TO "${schemaName}"`,
        label: `Set search path to "${schemaName}"`,
        optional: false,
      })
    } else if (driverKey === 'sqlserver') {
      statements.push({
        sql: `IF NOT EXISTS (SELECT 1 FROM sys.schemas WHERE name = '${schemaName}') EXEC('CREATE SCHEMA [${schemaName}]')`,
        label: `Create schema [${schemaName}]`,
        optional: false,
      })
    }
  }

  // --- Drop tables (if overwrite) ---
  if (overwrite && deployTables) {
    // Drop in reverse order to handle dependencies
    const tableNames = convertedSchema.tables.map(t => t.name).reverse()
    for (const name of tableNames) {
      const q = driverKey === 'sqlserver' ? `[${name}]`
        : driverKey === 'mysql' ? `\`${name}\``
        : `"${name}"`

      if (driverKey === 'db2') {
        statements.push({
          sql: `DROP TABLE ${q}`,
          label: `Drop table ${name}`,
          optional: true, // may not exist
        })
      } else if (driverKey === 'postgresql') {
        statements.push({
          sql: `DROP TABLE IF EXISTS ${q} CASCADE`,
          label: `Drop table ${name}`,
          optional: false,
        })
      } else if (driverKey === 'sqlserver') {
        statements.push({
          sql: `IF OBJECT_ID('${name}', 'U') IS NOT NULL DROP TABLE ${q}`,
          label: `Drop table ${name}`,
          optional: false,
        })
      } else {
        statements.push({
          sql: `DROP TABLE IF EXISTS ${q}`,
          label: `Drop table ${name}`,
          optional: true,
        })
      }
    }
  }

  // --- Create tables ---
  if (deployTables) {
    for (const table of convertedSchema.tables) {
      statements.push({
        sql: table.convertedDdl,
        label: `Create table ${table.name}`,
        optional: false,
      })
    }
  }

  // --- Create indexes ---
  if (deployIndexes) {
    for (const index of convertedSchema.indexes) {
      statements.push({
        sql: index.rawDdl,
        label: `Create index ${index.name}`,
        optional: false,
      })
    }
  }

  // --- Stored procedures ---
  // KEY DIFFERENCE: each procedure is sent as a single statement (not split on ;)
  if (deployProcedures) {
    for (const proc of convertedSchema.procedures) {
      const procDdl = proc.convertedDdl || proc.rawDdl
      statements.push({
        sql: procDdl,
        label: `Create procedure ${proc.name}`,
        optional: false,
        isProcedure: true,
      })
    }
  }

  // Execute all statements
  const results = []
  let succeeded = 0
  let failed = 0
  let skipped = 0

  for (let i = 0; i < statements.length; i++) {
    const stmt = statements[i]

    const result = await executeStatement(
      connConfig,
      stmt.sql,
      stmt.label,
      onProgress,
      i,
      statements.length
    )

    if (result.success) {
      succeeded++
    } else if (stmt.optional) {
      skipped++
      // Optional statement failed — that's fine (e.g. DROP TABLE that doesn't exist)
    } else {
      failed++
    }

    results.push({
      ...result,
      label: stmt.label,
      optional: stmt.optional,
    })
  }

  return {
    totalStatements: statements.length,
    succeeded,
    failed,
    skipped,
    results,
    success: failed === 0,
  }
}

// ---------------------------------------------------------------------------
// Re-deploy only failed statements
// ---------------------------------------------------------------------------
export async function redeployFailed(failedResults, connection, onProgress = () => {}) {
  const connConfig = buildConnectionConfig(connection)

  const results = []
  let succeeded = 0
  let failed = 0

  for (let i = 0; i < failedResults.length; i++) {
    const stmt = failedResults[i]

    const result = await executeStatement(
      connConfig,
      stmt.sql,
      stmt.label,
      onProgress,
      i,
      failedResults.length
    )

    if (result.success) {
      succeeded++
    } else {
      failed++
    }

    results.push({
      ...result,
      label: stmt.label,
      optional: false,
    })
  }

  return {
    totalStatements: failedResults.length,
    succeeded,
    failed,
    skipped: 0,
    results,
    success: failed === 0,
  }
}

// ---------------------------------------------------------------------------
// Test the target connection
// ---------------------------------------------------------------------------
export async function testTargetConnection(connection) {
  const connConfig = buildConnectionConfig(connection)
  return testConnection(connConfig)
}

// ---------------------------------------------------------------------------
// Export converted schema as SQL text
// ---------------------------------------------------------------------------
export function exportAsSql(convertedSchema, options = {}) {
  const { schemaName = '', includeDrops = false } = options
  const parts = []

  // Schema creation
  if (schemaName) {
    parts.push(`-- Create schema`)
    parts.push(`CREATE SCHEMA "${schemaName}" AUTHORIZATION db2inst1;`)
    parts.push(`SET CURRENT SCHEMA = '${schemaName}';`)
    parts.push('')
  }

  // Drop tables
  if (includeDrops) {
    parts.push('-- Drop existing tables')
    for (const table of [...convertedSchema.tables].reverse()) {
      parts.push(`DROP TABLE "${table.name}";`)
    }
    parts.push('')
  }

  // Create tables
  parts.push('-- Create tables')
  for (const table of convertedSchema.tables) {
    parts.push(table.convertedDdl)
    parts.push('')
  }

  // Indexes
  if (convertedSchema.indexes.length > 0) {
    parts.push('-- Create indexes')
    for (const index of convertedSchema.indexes) {
      parts.push(index.rawDdl)
      parts.push('')
    }
  }

  // Procedures
  if (convertedSchema.procedures.length > 0) {
    parts.push('-- Stored procedures')
    for (const proc of convertedSchema.procedures) {
      parts.push(proc.convertedDdl || proc.rawDdl)
      parts.push('')
    }
  }

  return parts.join('\n')
}
